'use client';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { PlayIcon } from '@hugeicons/core-free-icons';
import NextImage from '@/features/Image';
import axios from '@/lib/axios';

interface Video {
  id: string;
  slug: string;
  banner: string;
  title: string;
  orientation: string;
  is_popular: number;
}

interface Filter {
  label: string;
  params: {
    order_by?: string;
    is_popular?: string;
  };
}

export default function ZepidtubeVideos({
  initialVideos = [],
}: {
  initialVideos?: Video[];
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [videosData, setVideosData] = useState<Video[]>(initialVideos);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filters: Filter[] = [
    { label: 'Latest', params: { order_by: 'latest' } },
    { label: 'Popular', params: { is_popular: '1' } },
    { label: 'Oldest', params: { order_by: 'oldest' } },
  ];

  const fetchVideos = async (params: Record<string, string>) => {
    try {
      setLoading(true);
      setError(null);
      const queryString = new URLSearchParams(params).toString();
      const response = await axios.get(
        `/api/v2/youtube-videos?orientation=videos&${queryString}`
      );
      setVideosData(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch videos');
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterVideos = (index: number) => {
    setActiveIndex(index);
    const selectedFilter = filters[index];
    fetchVideos(selectedFilter.params);
  };

  // Load initial data if not provided
  useEffect(() => {
    if (initialVideos.length === 0) {
      fetchVideos({ order_by: 'latest' });
    }
  }, []);

  return (
    <div>
      <div className="container">
        <div className="videos padding-top padding-bottom">
          <div className="section-header mb-4">
            <h5 className="pt-0 pb-0 mb-0">Videos</h5>
            <div className="section-header__actions">
              {filters.map((filter, index) => (
                <button
                  key={index}
                  onClick={() => filterVideos(index)}
                  disabled={loading}
                  className={`btn btn-sm text-dark btn-outline ml-2 ${activeIndex === index ? 'border-black' : ''}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading videos...</div>
          ) : error ? (
            <div className="text-center py-4 text-danger">{error}</div>
          ) : videosData?.length > 0 ? (
            <div className="row g-4 justify-content-center">
              {videosData.map((video) => (
                <div className="col-md-6 col-lg-4" key={video.id}>
                  <article className="post">
                    <figure className="post__media post__video">
                      <Link href={`/zepid-tube/${video.slug}`}>
                        <NextImage
                          src={video?.banner}
                          alt={video?.title}
                          width={1280}
                          height={500}
                          className="fade-in fadeIn"
                          style={{
                            animationDuration: '0.3s',
                            animationDelay: '0s',
                          }}
                        />
                      </Link>
                      <Link
                        href={`/zepid-tube/${video.slug}`}
                        className="btn__video btn__iframe"
                      >
                        <HugeiconsIcon icon={PlayIcon} />
                      </Link>
                    </figure>
                    <div className="post__body">
                      <h5 className="post__title fw-semibold">
                        <Link
                          href={`/zepid-tube/${video.slug}`}
                          className="btn__video btn__iframe"
                        >
                          {video.title}
                        </Link>
                      </h5>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          ) : (
            <div className="row g-4 justify-content-center">
              <div className="col-md-6 col-lg-4">
                <h2 className="post__title">No videos available</h2>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
